// src/utils/supabaseUtils.js
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Function to determine the correct Supabase configuration
function getSupabaseConfig() {
  let supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  let supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // COMMENTED OUT - forcing localhost to use production Supabase
  // For local development, ensure we use local Supabase if available
  // if (import.meta.env.MODE === 'development' && 
  //     (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
  //   // Use local Supabase URL - key comes from environment
  //   const localSupabaseUrl = 'http://127.0.0.1:54321';
  //   console.log('DEVELOPMENT MODE: Using local Supabase:', localSupabaseUrl);
  //   supabaseUrl = localSupabaseUrl;
  //   // supabaseKey will be the local key from .env.local
  // }

  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Missing Supabase environment variables');
  }

  return { supabaseUrl, supabaseKey };
}

// Create a custom storage handler that ensures PKCE data is properly stored
const customStorage = {
  getItem: (key) => {
    return localStorage.getItem(key);
  },
  setItem: (key, value) => {
    localStorage.setItem(key, value);
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
  }
};

// Create a single Supabase client instance
let supabaseClient = null;

function createSupabaseClient() {
  if (supabaseClient) {
    return supabaseClient;
  }

  const { supabaseUrl, supabaseKey } = getSupabaseConfig();

  // Create Supabase client with enhanced auth options
  supabaseClient = createClient(supabaseUrl, supabaseKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: customStorage,
      debug: false // Disable debug mode to reduce logs
    }
  });

  // Disable excessive logging
  if (supabaseClient.auth && typeof supabaseClient.auth.setLogLevel === 'function') {
    // Set to 'error' to only show errors
    supabaseClient.auth.setLogLevel('error');
  }

  // Only log minimal info in development mode
  if (import.meta.env.MODE === 'development') {
    console.log('Environment:', import.meta.env.MODE);
    console.log('Supabase URL:', supabaseUrl);
    console.log('Current origin:', window.location.origin);
  }

  return supabaseClient;
}

// Export the single instance
export const supabase = createSupabaseClient();
export default supabase;

// Update user profile information
export const updateUserProfile = async (userData) => {
  try {
    // First update auth metadata
    const { error: authError } = await supabase.auth.updateUser({
      data: {
        given_name: userData.firstName,
        family_name: userData.lastName,
        full_name: `${userData.firstName} ${userData.lastName}`
      }
    });
    
    if (authError) throw authError;
    
    // Then update app_users table (removing the metadata field)
    const { data, error } = await supabase
      .from('app_users')
      .update({
        first_name: userData.firstName,
        last_name: userData.lastName,
        updated_at: new Date().toISOString()
        // Removing the metadata field that doesn't exist in the schema
      })
      .eq('id', userData.id)
      .select()
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Change user password
export const changeUserPassword = async (currentPassword, newPassword) => {
  try {
    // First verify the current password is correct
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    
    // Use the updateUser method to change password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) throw error;
    
    return { success: true, message: 'Password updated successfully' };
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// Template Functions
export const saveTemplate = async (templateData) => {
  const upsertData = { ...templateData };
  if (!upsertData.id) {
    upsertData.id = uuidv4();
  }

  // Ensure is_public is a boolean
  if (upsertData.is_public !== undefined) {
    upsertData.is_public = Boolean(upsertData.is_public);
  }

  const { data, error } = await supabase
    .from('templates')
    .upsert(upsertData, { onConflict: 'id' })
    .select()
    .single();

  if (error) {
    console.error("Error saving template:", error);
    throw error;
  }
  return data;
};

export const getTemplate = async (id) => {
  const { data, error } = await supabase.from('templates').select('*').eq('id', id).single();
  if (error) {
    console.error(`Error fetching template ${id}:`, error);
    throw error;
  }
  return data;
};

export const getAllTemplates = async () => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    console.error('No authenticated session for fetching templates');
    throw new Error('User not authenticated');
  }
  const userId = sessionData.session.user.id;

  // First check if user is admin
  const { data: userData, error: userError } = await supabase
    .from('app_users')
    .select('is_admin, user_type')
    .eq('id', userId)
    .single();

  if (userError) {
    console.error('Error checking admin status:', userError);
  }

  const isAdmin = userData && (userData.is_admin === true || userData.user_type === 'staff');
  
  let query = supabase.from('templates').select('*');
  
  // If user is admin, get all templates
  // Otherwise only get templates created by the user or public ones
  if (!isAdmin) {
    query = query.or(`user_id.eq.${userId},is_public.eq.true`);
  }

  const { data, error } = await query;
  console.log("getAllTemplates raw data:", data, "error:", error);
  if (error) throw error;
  return data || [];
};

// Order Functions
export const getOrderById = async (orderId) => {
  const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).single();
  if (error) {
    console.error(`Error fetching order ${orderId}:`, error);
    throw error;
  }
  return data;
};

// Asset Functions
export const getBackgroundAssets = async (sport = '', offset = 0, limit = 100) => {
  const path = sport ? `backgrounds/${sport}` : 'backgrounds';
  console.log(`Fetching backgrounds from path: ${path}, offset: ${offset}, limit: ${limit}`);

  const { data: folderData, error: folderError } = await supabase.storage
    .from('playmakerpics-images')
    .list(path, { limit, offset, sortBy: { column: 'name', order: 'asc' } });

  if (folderError) {
    console.error(`Error listing folders from ${path}:`, folderError);
    throw folderError;
  }

  let allAssets = [];
  if (!sport) {
    const subfolders = folderData.filter(f => !/\.(jpg|jpeg|png|gif)$/i.test(f.name) && f.name !== '.');
    for (const folder of subfolders) {
      const subPath = `${path}/${folder.name}`;
      const { data: fileData, error: fileError } = await supabase.storage
        .from('playmakerpics-images')
        .list(subPath, { limit, offset, sortBy: { column: 'name', order: 'asc' } });

      if (fileError) {
        console.error(`Error listing files from ${subPath}:`, fileError);
        continue;
      }

      const subAssets = fileData
        .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
        .map(file => {
          const encodedFileName = encodeURIComponent(file.name);
          const fullPath = `${subPath}/${encodedFileName}`;
          const { publicUrl } = supabase.storage.from('playmakerpics-images').getPublicUrl(fullPath).data;
          return { id: fullPath, uri: publicUrl, thumbUri: publicUrl };
        });
      allAssets = allAssets.concat(subAssets);
    }
  } else {
    allAssets = folderData
      .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
      .map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const fullPath = `${path}/${encodedFileName}`;
        const { publicUrl } = supabase.storage.from('playmakerpics-images').getPublicUrl(fullPath).data;
        return { id: fullPath, uri: publicUrl, thumbUri: publicUrl };
      });
  }

  return allAssets;
};

export const getAthleteImages = async () => {
  const path = 'athletes'; // Corrected path to match your storage structure
  console.log(`Fetching athlete images from path: ${path}`);
  const { data, error } = await supabase.storage.from('playmakerpics-images').list(path, {
    limit: 100,
    offset: 0,
    sortBy: { column: 'name', order: 'asc' }
  });
  if (error) {
    console.error(`Error listing athlete images from ${path}:`, error);
    throw error;
  }
  const assets = data
    .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
    .map(file => {
      const encodedFileName = encodeURIComponent(file.name);
      const fullPath = `${path}/${encodedFileName}`;
      const { publicUrl } = supabase.storage.from('playmakerpics-images').getPublicUrl(fullPath).data;
      return { id: file.name, uri: publicUrl, thumbUri: publicUrl };
    });
  return assets;
};

export const uploadImage = async (file, folder = 'uploads') => {
  const fileName = `${Date.now()}-${file.name}`;
  const path = `${folder}/${fileName}`;
  const { error } = await supabase.storage.from('playmakerpics-images').upload(path, file);
  if (error) {
    console.error(`Error uploading image to ${path}:`, error);
    throw error;
  }
  const { publicUrl } = supabase.storage.from('playmakerpics-images').getPublicUrl(path).data;
  return { uri: publicUrl, thumbUri: publicUrl };
};

export const logUserUpload = async (userId, fileUrl, watermarkedUrl = null, isPrimary = false) => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    console.error('No authenticated session for upload logging:', sessionError);
    throw new Error('User not authenticated');
  }
  const authenticatedUserId = sessionData.session.user.id;
  if (userId !== authenticatedUserId) {
    console.warn('User ID mismatch:', userId, authenticatedUserId);
    userId = authenticatedUserId; // Use session user ID
  }

  // Prepare the insert data
  const insertData = { 
    user_id: userId, 
    file_url: fileUrl,
    is_primary: isPrimary
  };
  
  // Add watermarked URL if provided
  if (watermarkedUrl) {
    insertData.file_url_wm = watermarkedUrl;
  }

  const { data, error } = await supabase
    .from('user_uploads')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error logging user upload:', error);
    throw error;
  }
  return data;
};

export async function getOrCreateAppUser() {
  console.log('Starting getOrCreateAppUser...');
  
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.error('No user found:', userError);
    throw new Error('User not authenticated');
  }

  console.log('User found:', user);
  console.log('User identities:', user.identities);
  console.log('User metadata:', user.user_metadata);
  console.log('App metadata:', user.app_metadata);

  // Determine login provider
  const provider = user.app_metadata?.provider || 
                  (user.identities && user.identities[0]?.provider) || 
                  'email';
  
  // Extract name from metadata or email
  let firstName = user.user_metadata?.given_name || '';
  let lastName = user.user_metadata?.family_name || '';
  
  // For email users without metadata, use email as fallback for name
  if ((!firstName || !lastName) && provider === 'email') {
    // Extract possible name from email (part before @)
    const emailName = user.email.split('@')[0];
    // Try to split by common separators
    const nameParts = emailName.split(/[._-]/);
    if (nameParts.length >= 2) {
      firstName = firstName || nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
      lastName = lastName || nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
    } else {
      firstName = firstName || emailName;
      lastName = lastName || 'User';
    }
  }

  // If user is signing in with email, handle metadata appropriately
  if (provider === 'email') {
    try {
      // Check if user has any Google identities
      const hasGoogleIdentity = user.identities?.some(id => id.provider === 'google');
      
      if (hasGoogleIdentity) {
        console.log('User has Google identity, checking if we should unlink...');
        
        // Only unlink if the Google identity is from a different account
        const googleIdentity = user.identities.find(id => id.provider === 'google');
        if (googleIdentity && googleIdentity.identity_data?.email !== user.email) {
          console.log('Unlinking different Google account...');
          const { error: unlinkError } = await supabase.auth.unlinkIdentity({
            provider: 'google'
          });
          
          if (unlinkError) {
            console.error('Error unlinking Google identity:', unlinkError);
          } else {
            console.log('Successfully unlinked different Google account');
          }
        } else {
          console.log('Keeping Google identity as it matches the current email');
        }
      }

      // Create a clean metadata object for email login
      const cleanMetadata = {
        given_name: firstName,
        family_name: lastName,
        full_name: `${firstName} ${lastName}`,
        email: user.email,
        email_verified: user.email_verified || false
      };

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: cleanMetadata
      });
      
      if (updateError) {
        console.error('Error updating user metadata:', updateError);
      } else {
        console.log('Successfully updated user metadata for email login');
        // Update the user object with our clean metadata
        user.user_metadata = cleanMetadata;
      }
    } catch (error) {
      console.error('Error handling user metadata:', error);
    }
  } else if (provider === 'google') {
    // For Google login, we'll keep the Google metadata
    console.log('User logged in with Google, preserving Google metadata');
  }

  // Determine user type
  const userType = determineUserType(user);

  // Prepare user data with defaults for all required fields
  const userData = {
    id: user.id,
    email: user.email,
    first_name: firstName, 
    last_name: lastName,
    user_type: userType,
    is_admin: userType === 'staff', // Set is_admin based on user type
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    metadata: JSON.stringify({
      full_name: `${firstName} ${lastName}`,
      given_name: firstName,
      family_name: lastName,
      avatar_url: null,
      provider: provider
    })
  };

  console.log('Prepared user data:', userData);

  try {
    console.log('Attempting to upsert user...');
    
    // First check if user already exists
    const { data: existingUser, error: fetchError } = await supabase
      .from('app_users')
      .select('*')
      .eq('id', user.id)
      .single();
      
    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found"
      console.error('Error fetching user:', fetchError);
    }
    
    console.log('Existing user data:', existingUser);
    
    let result;
    
    // If user exists, update
    if (existingUser) {
      console.log('User exists, updating...');
      const { data: updatedUser, error: updateError } = await supabase
        .from('app_users')
        .update({
          email: userData.email,
          updated_at: userData.updated_at,
          metadata: userData.metadata
        })
        .eq('id', user.id)
        .select()
        .single();
        
      if (updateError) {
        console.error('Error updating app user:', updateError);
        throw new Error(`Failed to update user profile: ${updateError.message}`);
      }
      
      result = updatedUser;
    } 
    // If user doesn't exist, insert
    else {
      console.log('User does not exist, inserting...');
      const { data: newUser, error: insertError } = await supabase
        .from('app_users')
        .insert(userData)
        .select()
        .single();
        
      if (insertError) {
        console.error('Error inserting app user:', insertError);
        console.error('Insert payload:', userData);
        throw new Error(`Failed to create user profile: ${insertError.message}`);
      }
      
      result = newUser;
    }

    console.log('User upserted successfully:', result);
    return result;
  } catch (error) {
    console.error('Error in getOrCreateAppUser:', error);
    throw error;
  }
}

export function determineUserType(user) {
  // Check if user is a Google identity
  const googleIdentity = user.identities?.find((id) => id.provider === 'google');
  if (googleIdentity) {
    return 'staff';
  }
  
  // Check if user is an email/password user
  const emailIdentity = user.identities?.find((id) => id.provider === 'email');
  
  // For email users, check if their email domain suggests they're staff
  // You can customize this logic based on your requirements
  if (emailIdentity && user.email) {
    // Example: if the email contains your company domain, treat as staff
    // Change 'yourcompany.com' to your actual company domain
    if (user.email.endsWith('@playmakerpics.com') || 
        user.email.endsWith('@playmaker.com') ||
        user.email.includes('admin') ||
        user.email.includes('staff')) {
      return 'staff';
    }
  }
  
  // Default to customer for all other users
  return 'customer';
}

/**
 * Log a user login to the database
 * This should be called after a successful authentication
 * 
 * @param {string} userId - The authenticated user's ID
 * @returns {Promise<Object>} The logged login record
 */
export const logUserLogin = async (userId = null) => {
  try {
    // If no userId is provided, get the current user
    if (!userId) {
      const { data } = await supabase.auth.getUser();
      if (!data || !data.user) {
        console.error('No authenticated user found');
        return null;
      }
      userId = data.user.id;
    }

    // Get client IP and user agent
    let ipAddress = 'unknown';
    let userAgent = navigator.userAgent || 'unknown';
    
    // Try to get IP from a service
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      }
    } catch (ipError) {
      console.warn('Could not determine client IP:', ipError);
    }

    // Use the log_user_login function
    const { data, error } = await supabase
      .rpc('log_user_login', { 
        p_user_id: userId,
        p_ip_address: ipAddress,
        p_user_agent: userAgent
      });

    if (error) {
      // If the RPC fails, try a direct insert
      console.warn('RPC log_user_login failed, attempting direct insert:', error);
      const { data: insertData, error: insertError } = await supabase
        .from('user_logins')
        .insert({
          user_id: userId,
          ip_address: ipAddress,
          user_agent: userAgent
        })
        .select()
        .single();
        
      if (insertError) {
        throw insertError;
      }
      return insertData;
    }
    
    return data;
  } catch (error) {
    console.error('Error logging user login:', error);
    // Return null but don't throw - we don't want to block the login process
    return null;
  }
};

// User preferences functions
export const getUserPreferences = async (userId) => {
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    userId = user.id;
  }
  
  try {
    // First check if user_preferences table exists by trying to query it
    const { error: tableCheckError } = await supabase
      .from('user_preferences')
      .select('id')
      .limit(1);
    
    // If table doesn't exist, create it
    if (tableCheckError && tableCheckError.code === '42P01') { // PostgreSQL code for undefined_table
      console.log('User preferences table does not exist, will use localStorage instead');
      return null;
    }
    
    // Get user preferences
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 is the "no rows returned" error code
      console.error('Error fetching user preferences:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getUserPreferences:', error);
    return null;
  }
};

export const updateUserPreferences = async (preferences) => {
  if (!preferences.user_id) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    preferences.user_id = user.id;
  }
  
  try {
    // First check if user_preferences table exists
    const { error: tableCheckError } = await supabase
      .from('user_preferences')
      .select('id')
      .limit(1);
    
    // If table doesn't exist, use localStorage only
    if (tableCheckError && tableCheckError.code === '42P01') {
      console.log('User preferences table does not exist, using localStorage only');
      
      // Save to localStorage
      Object.entries(preferences).forEach(([key, value]) => {
        if (key !== 'user_id' && value !== undefined) {
          localStorage.setItem(`playmaker_${key}`, JSON.stringify(value));
        }
      });
      
      return { success: true, message: 'Preferences saved to localStorage' };
    }
    
    // Otherwise, save to the database
    const { data, error } = await supabase
      .from('user_preferences')
      .upsert({
        ...preferences,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};